import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  Bot, Database, Boxes, Route, Workflow, Plug, Terminal, Crown, Compass, Megaphone,
  BrainCircuit, FlaskConical, Radar, ShoppingBag, Inbox, Building2, Activity, HeartPulse,
  Bell, CreditCard, ShieldCheck, PhoneCall, Users, Sparkles, Webhook, PlugZap, ShieldAlert,
} from 'lucide-react'

/**
 * AgentMesh — a live, animated constellation of the agent mesh.
 *
 * The Chief sits at the centre; every other agent orbits it, joined by a wire that
 * "flows" (marching dashes) when that agent is active or has recent run activity.
 * Click any node to see what it does, when it runs, what it provides, and the
 * sub-agents (swarm) it commands. Purpose-built for AGENTS (unlike BrainConstellation,
 * which visualises data connectors).
 *
 * Props:
 *  - agents:   [{ service, agentName, className, icon, role, trigger, provides[], swarm[], status, lastActiveAt }]
 *              (Chief first; from GET /tenants/:ns/agents or the platform agent catalog)
 *  - runsUrl:  endpoint for the run log used to animate live handoff edges (default
 *              '/api/v1/agent/runs'; pass null to disable, e.g. the platform view)
 *  - title/subtitle: header copy.
 */

const ICONS = {
  crown: Crown, compass: Compass, bot: Bot, megaphone: Megaphone, database: Database,
  boxes: Boxes, route: Route, workflow: Workflow, plug: Plug, terminal: Terminal,
  brain: BrainCircuit, flask: FlaskConical, radar: Radar, shopping: ShoppingBag,
  inbox: Inbox, building: Building2, activity: Activity, heart: HeartPulse, bell: Bell,
  card: CreditCard, shield: ShieldCheck, phone: PhoneCall,
  // Aliases for the long lucide-style names the agent CATALOG uses, so these nodes
  // show their real glyph instead of the generic Bot fallback.
  'shopping-cart': ShoppingBag, webhook: Webhook, 'brain-circuit': BrainCircuit,
  'flask-conical': FlaskConical, 'plug-zap': PlugZap, 'credit-card': CreditCard,
  'shield-check': ShieldCheck, 'shield-alert': ShieldAlert,
}
const iconFor = (k) => ICONS[k] || Bot

const STATUS = {
  active: { color: '#16a34a', label: 'Active' },
  idle: { color: '#9ca3af', label: 'Idle' },
  error: { color: '#dc2626', label: 'Needs attention' },
}
const statusOf = (s) => STATUS[s] || STATUS.idle

const W = 760, H = 520, CX = W / 2, CY = H / 2

export default function AgentMesh({ agents = [], runsUrl = '/api/v1/agent/runs', title = 'Your agent mesh', subtitle }) {
  const [runs, setRuns] = useState([])
  const [selKey, setSelKey] = useState(null)

  useEffect(() => {
    if (!runsUrl) return
    let alive = true
    axios.get(runsUrl, { params: { limit: 50 } })
      .then((r) => { if (alive) setRuns(r.data?.runs || r.data || []) })
      .catch(() => {})
    return () => { alive = false }
  }, [runsUrl])

  const chief = agents.find((a) => a.service === 'chief') || agents[0] || null
  const others = agents.filter((a) => a && a !== chief)

  // Agents with recent runs → their wire flows.
  const activeEdges = useMemo(() => {
    const s = new Set()
    for (const r of runs || []) { if (r.agent) s.add(r.agent); if (r.sub_agent) s.add(r.sub_agent) }
    return s
  }, [runs])

  // Ring layout for the orbiting agents.
  const R = Math.min(W, H) * 0.37
  const nodes = others.map((a, i) => {
    const ang = (2 * Math.PI * i) / Math.max(others.length, 1) - Math.PI / 2
    return { a, x: CX + R * Math.cos(ang), y: CY + R * Math.sin(ang), flowing: activeEdges.has(a.service) || a.status === 'active' }
  })

  const selected = selKey ? agents.find((a) => a.service === selKey) : null
  const pct = (v, tot) => `${(v / tot) * 100}%`

  return (
    <section className="card p-0 overflow-hidden">
      <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Sparkles size={18} className="text-primary-600" /> {title}</h3>
          {subtitle && <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-gray-500">
          {Object.entries(STATUS).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: v.color }} /> {v.label}</span>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_310px]">
        {/* ── Constellation ─────────────────────────────────────────────── */}
        <div className="relative bg-gradient-to-b from-gray-50/60 to-white" style={{ width: '100%', aspectRatio: `${W} / ${H}` }}>
          {/* wires (SVG under the nodes) */}
          <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 w-full h-full" aria-hidden="true">
            {nodes.map(({ a, x, y, flowing }) => (
              <line key={a.service} x1={CX} y1={CY} x2={x} y2={y}
                stroke={flowing ? '#dc2626' : '#e5e7eb'} strokeWidth={flowing ? 1.6 : 1.2}
                className={flowing ? 'am-flow' : ''} strokeLinecap="round" opacity={flowing ? 0.85 : 0.7} />
            ))}
          </svg>

          {/* Chief node */}
          {chief && (
            <button type="button" onClick={() => setSelKey(chief.service)}
              className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none group"
              style={{ left: pct(CX, W), top: pct(CY, H) }} aria-label={chief.agentName}>
              <span className="am-breathe absolute inset-0 rounded-full" />
              <span className={`relative flex flex-col items-center justify-center w-[68px] h-[68px] rounded-full bg-primary-600 text-white shadow-lg ring-4 ring-primary-500/20 transition-transform group-hover:scale-105 ${selKey === chief.service ? 'ring-primary-500/40' : ''}`}>
                {React.createElement(iconFor(chief.icon), { size: 26 })}
              </span>
              <span className="block text-[11px] font-semibold text-gray-900 mt-1.5 whitespace-nowrap">{chief.agentName}</span>
            </button>
          )}

          {/* Agent nodes */}
          {nodes.map(({ a, x, y }) => {
            const Icon = iconFor(a.icon)
            const st = statusOf(a.status)
            const on = selKey === a.service
            return (
              <button type="button" key={a.service} onClick={() => setSelKey(a.service)}
                className="absolute -translate-x-1/2 -translate-y-1/2 focus:outline-none group"
                style={{ left: pct(x, W), top: pct(y, H) }} aria-label={a.agentName}>
                <span className={`relative flex items-center justify-center w-12 h-12 rounded-2xl bg-white border shadow-sm transition-transform group-hover:scale-110 ${on ? 'border-primary-400 ring-2 ring-primary-400/30' : 'border-gray-200'}`}>
                  <Icon size={20} className="text-primary-600" />
                  <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${a.status === 'active' ? 'animate-pulse' : ''}`} style={{ background: st.color }} />
                  {a.swarm?.length > 0 && (
                    <span className="absolute -bottom-1.5 -right-1.5 text-[9px] font-bold text-white bg-gray-700 rounded-full px-1 min-w-[15px] h-[15px] flex items-center justify-center">+{a.swarm.length}</span>
                  )}
                </span>
                <span className="block text-[10.5px] font-medium text-gray-700 mt-1 max-w-[92px] truncate mx-auto">{a.agentName}</span>
              </button>
            )
          })}
        </div>

        {/* ── Detail panel ──────────────────────────────────────────────── */}
        <aside className="border-t lg:border-t-0 lg:border-l border-gray-100 p-5 min-h-[220px]">
          {selected ? <NodeDetail a={selected} /> : (
            <div className="text-sm text-gray-500 h-full flex flex-col justify-center">
              <p className="font-medium text-gray-700 mb-1">{agents.length} agents in the mesh</p>
              <p className="text-[13px] leading-relaxed">The <span className="font-semibold text-primary-600">Chief</span> at the centre oversees every agent and dispatches them on demand. A <span className="text-primary-600 font-medium">red flowing wire</span> means that agent is active or recently ran. Click any node to see what it does and the sub-agents it commands.</p>
            </div>
          )}
        </aside>
      </div>

      <style>{`
        @keyframes am-dash { to { stroke-dashoffset: -20; } }
        .am-flow { stroke-dasharray: 3 5; animation: am-dash 0.9s linear infinite; }
        @keyframes am-breathe { 0%,100% { transform: scale(1); opacity:.35 } 50% { transform: scale(1.35); opacity:0 } }
        .am-breathe { background: radial-gradient(circle, rgba(220,38,38,.5) 0%, transparent 70%); animation: am-breathe 3.2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .am-flow, .am-breathe { animation: none; } }
      `}</style>
    </section>
  )
}

function NodeDetail({ a }) {
  const Icon = iconFor(a.icon)
  const st = statusOf(a.status)
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        <span className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center"><Icon size={20} /></span>
        <div className="min-w-0">
          <h4 className="font-bold text-gray-900 leading-tight">{a.agentName}</h4>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: st.color }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: st.color }} /> {st.label}</span>
        </div>
      </div>
      <p className="text-[13px] text-gray-600 leading-relaxed">{a.role}</p>
      {a.trigger && <p className="text-[12px] text-gray-400 flex items-start gap-1.5"><Sparkles size={12} className="mt-0.5 shrink-0" /> {a.trigger}</p>}
      {a.provides?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5">What it provides</p>
          <div className="flex flex-wrap gap-1.5">
            {a.provides.slice(0, 8).map((p, i) => <span key={i} className="text-[11px] bg-primary-50 text-primary-700 rounded-full px-2 py-0.5">{p}</span>)}
          </div>
        </div>
      )}
      {a.swarm?.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold mb-1.5 flex items-center gap-1.5"><Users size={11} /> Sub-agents it commands</p>
          <div className="space-y-1.5">
            {a.swarm.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-[12px]">
                <span className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 text-[9px] font-bold mt-0.5 ${s.specialty ? 'bg-purple-100 text-purple-600' : 'bg-primary-100 text-primary-700'}`}>{i + 1}</span>
                <span><span className="font-medium text-gray-800">{s.name}</span>{s.does ? <span className="text-gray-400"> — {s.does}</span> : null}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
